import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DollarSign, Download, TrendingUp, ChevronLeft, ChevronRight,
  Filter, Building2, CreditCard, Users, Shield, ArrowUpRight,
  ArrowDownRight, Wallet, RefreshCw, Eye, ShieldAlert,
} from 'lucide-react';
import api from '../../utils/api';
import DateField from '../../components/DateField';
import AccountingGradeReports from '../../components/AccountingGradeReports';
import { useRBAC } from '../../context/RBACContext';
import { useTranslation } from 'react-i18next';

// Mirror of backend `utils/tenant_scope._PLATFORM_FINANCE_ROLES`. Holding a
// finance permission for your OWN clinic never implies platform-wide scope.
const PLATFORM_FINANCE_ROLES = ['treatx_super_admin', 'treatx_admin', 'treatx_accountant'];


function getToken() {
  return '';
}
function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

const STATUS_COLORS = {
  completed: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-emerald-100 text-emerald-700',
  funded: 'bg-emerald-100 text-emerald-700',
  held: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  expected: 'bg-amber-100 text-amber-700',
  draft: 'bg-slate-100 text-slate-600',
  unknown: 'bg-slate-100 text-slate-600',
  initiated: 'bg-amber-100 text-amber-700',
};

const MOVEMENT_LABELS = {
  payment_received: { label: 'Payment Received', icon: ArrowDownRight, color: 'text-emerald-600' },
  escrow_funded: { label: 'Escrow Funded', icon: Shield, color: 'text-blue-600' },
  advance_received: { label: 'Advance Expected', icon: Wallet, color: 'text-amber-600' },
  commission_accrued: { label: 'Commission', icon: TrendingUp, color: 'text-violet-600' },
  clinic_payout_due: { label: 'Payout Due', icon: ArrowUpRight, color: 'text-orange-600' },
  subscription_payment: { label: 'Subscription', icon: CreditCard, color: 'text-cyan-600' },
};

const CP_TYPES = [
  { value: 'all', label: 'All counterparties' },
  { value: 'clinic', label: 'Clinics' },
  { value: 'patient', label: 'Patients' },
  { value: 'treatx', label: 'TreatX' },
  { value: 'doctor', label: 'Doctors' },
];

const MOVEMENT_TYPES = [
  { value: 'all', label: 'All movements' },
  { value: 'payment_received', label: 'Payments received' },
  { value: 'escrow_funded', label: 'Escrow funded' },
  { value: 'advance_received', label: 'Advances expected' },
  { value: 'commission_accrued', label: 'Commissions' },
  { value: 'clinic_payout_due', label: 'Payouts due' },
  { value: 'subscription_payment', label: 'Subscriptions' },
];

function getDatePreset(key) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  if (key === 'mtd') return { from: new Date(y, m, 1).toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
  if (key === 'qtd') {
    const qStart = new Date(y, Math.floor(m / 3) * 3, 1);
    return { from: qStart.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
  }
  if (key === 'ytd') return { from: new Date(y, 0, 1).toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
  return { from: '', to: '' };
}

function PositionCard({ title, amount, subtitle, icon: Icon, color }) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{title}</p>
            <p className="text-xl font-bold mt-1 truncate">
              EUR {(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
            {subtitle && <p className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AccountingDashboard() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [balances, setBalances] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  const [counterparties, setCounterparties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // P0-FIN-REPORT-SCOPE-01 — the reporting scope is read from the CANONICAL
  // active RBAC context, never from the login-time `treatx_unified_user`
  // snapshot. That snapshot is written once at login and is NOT rewritten on
  // a workspace/context switch, and it carries `role: pending, clinic_id:
  // null` for every multi-workspace principal (restricted context-selection
  // bootstrap session), which is why a Clinic Admin was shown "Platform
  // Level / All figures are across all clinics and patients".
  //
  // Fail-closed: "Platform" is claimed ONLY for the explicitly authorised
  // platform-finance roles, mirroring backend `_PLATFORM_FINANCE_ROLES`
  // (utils/tenant_scope.py). Anything else is clinic scope, or — with no
  // resolved clinic — an explicitly UNRESOLVED scope that never asserts
  // platform-wide coverage.
  const { activeClinicId, activeRoleId, can } = useRBAC();
  const isPlatformScoped = PLATFORM_FINANCE_ROLES.includes(activeRoleId);
  const isClinicScoped = !isPlatformScoped && !!activeClinicId;
  const scopeState = isPlatformScoped ? 'platform' : (isClinicScoped ? 'clinic' : 'unresolved');
  const canExport = can('reporting.export');

  const [clinicDisplayName, setClinicDisplayName] = React.useState('');
  React.useEffect(() => {
    setClinicDisplayName('');
    if (!isClinicScoped || !activeClinicId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/public/catalog/clinics/${activeClinicId}`);
        const data = res.data;
        if (!cancelled && data.found && data.clinic?.name) setClinicDisplayName(data.clinic.name);
      } catch (err) { console.error('Failed to fetch clinic name:', err); }
    })();
    return () => { cancelled = true; };
  }, [isClinicScoped, activeClinicId]);

  // Filters
  const [cpType, setCpType] = useState('all');
  // OWNER-TEST-BLOCKER-FIX-PACK: Radix Select.Item forbids empty-string values.
  // Use sentinel "all" to mean "no counterparty filter".
  const [cpId, setCpId] = useState('all');
  const [movementType, setMovementType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [datePreset, setDatePreset] = useState('');

  // Statement
  const [statementTarget, setStatementTarget] = useState(null);
  const [statement, setStatement] = useState(null);

  const applyPreset = (key) => {
    setDatePreset(key);
    if (key === 'custom' || !key) { setFromDate(''); setToDate(''); return; }
    const p = getDatePreset(key);
    setFromDate(p.from);
    setToDate(p.to);
  };

  // P0-FIN-REPORT-SCOPE-01 — with no resolved canonical scope we do not
  // even ASK for financial data. The backend already fails closed
  // (403 forbidden_no_clinic_scope), but an unresolved context must show
  // nothing at all rather than briefly rendering a platform-shaped empty
  // report.
  const scopeResolved = isPlatformScoped || isClinicScoped;

  // P0-FIN-REPORT-SCOPE-01 / ACCOUNTING-TRUTH-01 (E) — the canonical active
  // scope, and a ref kept in step with it during render so that a response
  // which arrives AFTER a workspace switch can be recognised as superseded and
  // dropped. Clearing state on switch is not enough on its own: an in-flight
  // request for clinic A can land after the switch to clinic B and repaint A's
  // figures under B's header.
  const scopeKey = `${activeRoleId || 'none'}:${activeClinicId || 'none'}`;
  const scopeKeyRef = React.useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  const superseded = (token) => scopeKeyRef.current !== token;

  const fetchSummary = useCallback(async () => {
    if (!scopeResolved) { setSummary(null); return; }
    const token = scopeKey;
    try {
      const params = new URLSearchParams();
      if (cpType !== 'all' && cpId && cpId !== 'all') params.set('clinic_id', cpId);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const res = await api.get(`/accounting/summary?${params}`);
      if (superseded(token)) return;
      setSummary(res.data);
    } catch (err) { console.error('Summary fetch:', err); }
  }, [cpType, cpId, fromDate, toDate, scopeKey, scopeResolved]);

  const fetchBalances = useCallback(async () => {
    if (!scopeResolved) { setBalances(null); return; }
    const token = scopeKey;
    try {
      const res = await api.get(`/accounting/balances`);
      if (superseded(token)) return;
      if (res.status === 403) {
        // P0-2 FIX: Clinic users cannot access platform-wide balances — expected behavior.
        // Set null so the UI hides the position cards section gracefully.
        setBalances(null);
        return;
      }
      setBalances(res.data);
    } catch (err) { console.error('Balances fetch:', err); }
  }, [scopeKey, scopeResolved]);

  const fetchTransactions = useCallback(async (page = 1) => {
    if (!scopeResolved) { setTransactions([]); setLoading(false); return; }
    const token = scopeKey;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '30' });
      if (cpType !== 'all') params.set('counterparty_type', cpType);
      if (cpId && cpId !== 'all') params.set('counterparty_id', cpId);
      if (movementType !== 'all') params.set('movement_type', movementType);
      if (statusFilter) params.set('status', statusFilter);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const res = await api.get(`/accounting/transactions?${params}`);
      if (superseded(token)) return;
      {
        const d = res.data;
        setTransactions(d.transactions || []);
        setPagination({ page: d.page, total: d.total, total_pages: d.total_pages });
      }
    } catch (err) { console.error('Transactions fetch:', err); }
    finally { if (!superseded(token)) setLoading(false); }
  }, [cpType, cpId, movementType, statusFilter, fromDate, toDate, scopeKey, scopeResolved]);

  const fetchCounterparties = useCallback(async () => {
    if (!scopeResolved) { setCounterparties([]); return; }
    const token = scopeKey;
    try {
      const params = new URLSearchParams();
      if (cpType !== 'all') params.set('counterparty_type', cpType);
      const res = await api.get(`/accounting/counterparties?${params}`);
      if (superseded(token)) return;
      setCounterparties(res.data);
    } catch (err) { console.error('Counterparties fetch:', err); }
  }, [cpType, scopeKey, scopeResolved]);

  const fetchStatement = useCallback(async (type, id) => {
    const token = scopeKey;
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const res = await api.get(`/accounting/counterparty/${type}/${id}/statement?${params}`);
      if (superseded(token)) return;
      {
        setStatement(res.data);
        setStatementTarget({ type, id });
        setTab('statement');
      }
    } catch (err) { console.error('Statement fetch:', err); }
  }, [fromDate, toDate, scopeKey]);

  useEffect(() => { fetchSummary(); fetchBalances(); }, [fetchSummary, fetchBalances]);
  useEffect(() => { fetchTransactions(1); }, [fetchTransactions]);
  useEffect(() => { fetchCounterparties(); }, [fetchCounterparties]);

  // Every financial slice is dropped the moment the canonical active context
  // changes; the callbacks above then refetch because they depend on `scopeKey`,
  // and any response from the previous scope is discarded as superseded.
  const previousScopeKey = React.useRef(scopeKey);
  useEffect(() => {
    if (previousScopeKey.current === scopeKey) return;
    previousScopeKey.current = scopeKey;
    setSummary(null);
    setBalances(null);
    setTransactions([]);
    setCounterparties([]);
    setPagination({ page: 1, total: 0, total_pages: 0 });
    setStatement(null);
    setStatementTarget(null);
    setCpId('all');
    setTab('overview');
    setLoading(true);
  }, [scopeKey]);

  // Clear statement when counterparty filter changes
  useEffect(() => {
    setStatement(null);
    setStatementTarget(null);
    if (tab === 'statement') setTab('counterparties');
  }, [cpType, cpId]);

  // Clear statement when switching tabs
  const handleTabChange = (newTab) => {
    if (newTab !== 'statement') {
      setStatement(null);
      setStatementTarget(null);
    }
    setTab(newTab);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format: 'csv' });
      if (cpType !== 'all') params.set('counterparty_type', cpType);
      if (movementType !== 'all') params.set('movement_type', movementType);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const res = await api.get(`/accounting/export?${params}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = 'treatx_accounting_export.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Export:', err); }
    finally { setExporting(false); }
  };

  const fmtDate = (d) => d ? formatDateLocale(d) : '--';
  const fmtAmt = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
  // P0-FIN-REPORT-ACCURACY-01 — never print a hardcoded "EUR". The server
  // states a single currency only when the figures really are in one; a
  // mixed aggregation is labelled MIXED and disclosed below.
  const ccy = summary?.currency || (summary?.cross_currency_aggregation ? 'MIXED' : '');

  return (
    <div className="min-h-screen bg-gray-50" data-testid="accounting-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="accounting-title">Financial Overview</h1>
            <p className="text-sm text-muted-foreground">Read-only accounting derived from TreatX transaction data</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { fetchSummary(); fetchBalances(); fetchTransactions(1); }} data-testid="refresh-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            {canExport && (
              <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-csv-btn">
                <Download className="h-3.5 w-3.5 mr-1" /> {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit max-w-full overflow-x-auto" data-testid="tab-bar">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'movements', label: 'Movements' },
            { key: 'counterparties', label: 'Counterparties' },
            { key: 'reports', label: 'Accounting Reports' },
            ...(statement ? [{ key: 'statement', label: `Statement: ${statement.counterparty_name}` }] : []),
          ].map(t => (
            <button
              key={t.key}
              onClick={() => handleTabChange(t.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === t.key ? 'bg-white shadow-sm text-gray-900' : 'text-muted-foreground hover:text-gray-700'}`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Scope Indicator — P0-FIN-REPORT-SCOPE-01: canonical active context */}
        <div className="flex items-center gap-2 mb-1" data-testid="accounting-scope-indicator">
          <Badge variant="outline" data-testid={`accounting-scope-${scopeState}`}
            className={`text-[10px] ${scopeState === 'clinic'
              ? 'bg-teal-50 text-teal-700 border-teal-200'
              : scopeState === 'platform'
                ? 'bg-blue-50 text-blue-700 border-blue-200'
                : 'bg-slate-100 text-slate-600 border-slate-300'}`}>
            {t(`accountingScope.badge.${scopeState}`)}
          </Badge>
          <span className="text-[10px] text-muted-foreground" data-testid="accounting-scope-note">
            {scopeState === 'clinic'
              ? t('accountingScope.note.clinic', { clinic: clinicDisplayName || activeClinicId })
              : t(`accountingScope.note.${scopeState}`)}
          </span>
        </div>

        {/* Date Presets + Filters (shared) */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex flex-wrap items-center gap-2" data-testid="filters-bar">
              <span className="text-xs font-medium text-muted-foreground mr-1">Period:</span>
              {['mtd', 'qtd', 'ytd'].map(p => (
                <Button key={p} size="sm" variant={datePreset === p ? 'default' : 'outline'}
                  className="h-7 text-[11px] px-2.5" onClick={() => applyPreset(p)}
                  data-testid={`preset-${p}`}>
                  {p.toUpperCase()}
                </Button>
              ))}
              <DateField value={fromDate} onChange={(iso) => { setFromDate(iso); setDatePreset('custom'); }}
                className="w-[130px]" inputClassName="h-7 text-xs" data-testid="filter-from-date" />
              <DateField value={toDate} onChange={(iso) => { setToDate(iso); setDatePreset('custom'); }}
                className="w-[130px]" inputClassName="h-7 text-xs" data-testid="filter-to-date" />
              <div className="h-5 w-px bg-border mx-1" />
              <Select value={cpType} onValueChange={setCpType}>
                <SelectTrigger className="w-[150px] h-7 text-xs" data-testid="filter-cp-type"><SelectValue /></SelectTrigger>
                <SelectContent>{CP_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={movementType} onValueChange={setMovementType}>
                <SelectTrigger className="w-[170px] h-7 text-xs" data-testid="filter-movement-type"><SelectValue /></SelectTrigger>
                <SelectContent>{MOVEMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              {cpType !== 'all' && counterparties.length > 0 && (
                <Select value={cpId} onValueChange={setCpId}>
                  <SelectTrigger className="w-[200px] h-7 text-xs" data-testid="filter-cp-id">
                    <SelectValue placeholder="All counterparties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {counterparties.map(cp => (
                      <SelectItem key={cp.counterparty_id} value={cp.counterparty_id}>
                        {cp.counterparty_name || cp.counterparty_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* P0-FIN-REPORT-SCOPE-01 — unresolved canonical context: show
            nothing financial, say why. */}
        {!scopeResolved && (
          <Card className="border-slate-300 bg-slate-50" data-testid="scope-unresolved-panel">
            <CardContent className="py-8 text-center space-y-2">
              <ShieldAlert className="h-6 w-6 mx-auto text-slate-500" />
              <div className="text-sm font-medium text-slate-700">
                {t('accountingScope.badge.unresolved')}
              </div>
              <div className="text-xs text-slate-600 max-w-md mx-auto">
                {t('accountingScope.note.unresolved')}
              </div>
            </CardContent>
          </Card>
        )}

        {/* === OVERVIEW TAB === */}
        {scopeResolved && tab === 'overview' && (
          <>
            {/* Position Cards */}
            {balances && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="balance-cards">
                <PositionCard title="Received" amount={balances.total_received_from_patients} subtitle="From patients" icon={ArrowDownRight} color="bg-emerald-500" />
                <PositionCard title="Receivable" amount={balances.total_receivable_from_patients} subtitle="Expected from patients" icon={Wallet} color="bg-amber-500" />
                <PositionCard title="Escrow Held" amount={balances.escrow_current_balance} subtitle="In escrow" icon={Shield} color="bg-blue-500" />
                <PositionCard title="Payable" amount={balances.total_payable_to_clinics} subtitle="Due to clinics" icon={ArrowUpRight} color="bg-orange-500" />
                <PositionCard title="Commissions" amount={balances.total_commission_earned} subtitle="TreatX earned" icon={TrendingUp} color="bg-violet-500" />
                <PositionCard title="Subscriptions" amount={balances.total_subscription_revenue} subtitle="Recurring revenue" icon={CreditCard} color="bg-cyan-500" />
              </div>
            )}
            {balances?.limitations && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-800" data-testid="limitations-note">
                <span className="font-medium">Data limitations: </span>
                {balances.limitations.join(' | ')}
              </div>
            )}
            {/* Summary breakdown */}
            {summary?.cross_currency_aggregation && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-xs text-red-800"
                data-testid="cross-currency-warning">
                <span className="font-medium">{t('accountingScope.mixedCurrency.title')} </span>
                {t('accountingScope.mixedCurrency.body', {
                  currencies: (summary.currencies_present || []).join(', '),
                })}
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="currency-breakdown">
                  {Object.entries(summary.currency_breakdown || {}).map(([cur, v]) => (
                    <div key={cur} className="rounded border border-red-200 bg-white px-2 py-1"
                      data-testid={`currency-breakdown-${cur}`}>
                      <div className="font-semibold">{cur}</div>
                      <div>{t('accountingScope.mixedCurrency.received')}: {fmtAmt(v.total_received)}</div>
                      <div>{t('accountingScope.mixedCurrency.payable')}: {fmtAmt(v.total_payable)}</div>
                      {/* ACCOUNTING-TRUTH-02 (1) — escrow custody is shown as
                          its own component. It restates money already counted
                          as received and was previously added to it, which
                          doubled the figure a clinic saw. */}
                      {v.escrow_funded > 0 && (
                        <div className="text-muted-foreground"
                          data-testid={`currency-breakdown-${cur}-escrow`}>
                          {t('accountingScope.mixedCurrency.escrow')}: {fmtAmt(v.escrow_funded)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {summary.currency_breakdown
                  && Object.values(summary.currency_breakdown)[0]?.components_note && (
                  <div className="mt-2 text-[11px]" data-testid="currency-components-note">
                    {Object.values(summary.currency_breakdown)[0].components_note}
                  </div>
                )}
              </div>
            )}
            {summary && (
              <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-2.5 text-xs text-sky-900"
                data-testid="operational-basis-note">
                <span className="font-medium">{t('accountingBasis.operational.title')} </span>
                {t('accountingBasis.operational.body')}
              </div>
            )}
            {summary?.completeness?.complete === false && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-2.5 text-xs text-amber-900"
                data-testid="summary-incompleteness-note">
                <span className="font-medium">
                  {t('accountingCompleteness.title', {
                    count: summary.completeness.amounts_excluded_count,
                  })}{' '}
                </span>
                {t('accountingCompleteness.body')}
                {summary.completeness.amounts_excluded_reasons?.length > 0 && (
                  <span className="block mt-1 font-mono text-[11px]"
                    data-testid="summary-incompleteness-reasons">
                    {summary.completeness.amounts_excluded_reasons.join(' · ')}
                  </span>
                )}
              </div>
            )}
            {summary && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Payment Flow</CardTitle></CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Payments received</span><span className="font-medium">{ccy} {fmtAmt(summary.payments?.total_received)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Escrow funded</span><span className="font-medium">{ccy} {fmtAmt(summary.escrow?.total_funded)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Escrow held (net)</span><span className="font-medium text-blue-600">{ccy} {fmtAmt(summary.escrow?.current_held)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Contract balance still to collect</span><span className="font-medium text-amber-600">{ccy} {fmtAmt(summary.advances?.total_expected)}</span></div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-medium">Clinic payouts due (contractual)</span>
                      <span className="font-bold text-orange-600">
                        {ccy} {fmtAmt(summary.clinic_payouts?.total_due)}
                        {summary.clinic_payouts?.complete === false && (
                          <span className="ml-1 text-[11px] font-normal text-amber-700"
                            data-testid="clinic-payouts-incomplete">
                            {t('accountingCompleteness.partial', {
                              count: summary.clinic_payouts.amounts_excluded_count,
                            })}
                          </span>
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
                {/* ACCOUNTING-TRUTH-01 (B3) — a clinic sees its OWN perspective.
                    The platform-wide "TreatX Revenue" card is not the clinic's
                    income and its subscription charge is a cost, not revenue. */}
                {summary.perspective === 'provider_clinic' ? (
                  <Card data-testid="clinic-perspective-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">{t('accountingClinicView.title')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('accountingClinicView.collected')}</span><span className="font-medium">{ccy} {fmtAmt(summary.clinic_view?.collected_from_patients_by_treatx)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('accountingClinicView.outstanding')}</span><span className="font-medium text-amber-600">{ccy} {fmtAmt(summary.clinic_view?.contractual_balance_still_to_collect)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('accountingClinicView.providerShare')}</span><span className="font-medium text-emerald-700">{ccy} {fmtAmt(summary.clinic_view?.provider_share_contractual)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('accountingClinicView.commission')}</span><span className="font-medium">{ccy} {fmtAmt(summary.clinic_view?.commission_charged_by_treatx)}</span></div>
                      <div className="border-t pt-2 flex justify-between">
                        <span className="font-medium">{t('accountingClinicView.subscriptionExpense')}</span>
                        <span className="font-bold text-rose-600">{ccy} {fmtAmt(summary.clinic_view?.subscription_expense_to_treatx)}</span>
                      </div>
                      <ul className="text-[11px] text-muted-foreground list-disc pl-4 pt-1 space-y-0.5"
                        data-testid="clinic-perspective-notes">
                        {(summary.clinic_view?.notes || []).map((n, i) => <li key={i}>{n}</li>)}
                      </ul>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">TreatX Revenue</CardTitle></CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Commissions accrued</span><span className="font-medium">{ccy} {fmtAmt(summary.commissions?.total_accrued)} <span className="text-xs text-muted-foreground">({summary.commissions?.count})</span></span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Subscription revenue</span><span className="font-medium">{ccy} {fmtAmt(summary.subscriptions?.total_amount)} <span className="text-xs text-muted-foreground">({summary.subscriptions?.count})</span></span></div>
                      <div className="border-t pt-2 flex justify-between"><span className="font-medium">Net TreatX revenue</span><span className="font-bold text-violet-600">{ccy} {fmtAmt(summary.positions?.net_treatx_revenue)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Total receivable</span><span className="font-medium">{ccy} {fmtAmt(summary.positions?.total_receivable)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Total payable</span><span className="font-medium">{ccy} {fmtAmt(summary.positions?.total_payable)}</span></div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </>
        )}

        {/* === MOVEMENTS TAB === */}
        {tab === 'movements' && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Movements ({pagination.total})</CardTitle>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  Page {pagination.page}/{pagination.total_pages || 1}
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={pagination.page <= 1} onClick={() => fetchTransactions(pagination.page - 1)} data-testid="pagination-prev"><ChevronLeft className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={pagination.page >= pagination.total_pages} onClick={() => fetchTransactions(pagination.page + 1)} data-testid="pagination-next"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="transaction-table">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Movement</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Reference</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Counterparty</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Debit</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Credit</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Status</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                    ) : transactions.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No movements found</td></tr>
                    ) : transactions.map((tx, i) => {
                      const ml = MOVEMENT_LABELS[tx.movement_type] || {};
                      const MIcon = ml.icon || DollarSign;
                      return (
                        <tr key={`${tx.reference}-${i}`} className="border-b hover:bg-muted/20 transition-colors" data-testid={`tx-row-${i}`}>
                          <td className="px-3 py-2">
                            <div className={`flex items-center gap-1.5 ${ml.color || ''}`}>
                              <MIcon className="h-3 w-3" />
                              <span className="font-medium">{tx.description_label || ml.label || tx.movement_type}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 font-mono text-[10px]">{tx.reference}</td>
                          <td className="px-3 py-2 text-muted-foreground">{fmtDate(tx.date)}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col">
                              <span className="font-medium truncate max-w-[140px]"
                                title={tx.counterparty_id}>
                                {tx.counterparty_name}
                                {tx.counterparty_name_masked && (
                                  <span className="ml-1 text-[9px] text-muted-foreground"
                                    data-testid={`tx-name-masked-${i}`}>(masked)</span>
                                )}
                              </span>
                              <span className="text-[10px] text-muted-foreground capitalize">
                                {tx.counterparty_brand_name || tx.counterparty_type}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {tx.amount_available === false
                              ? <span className="text-amber-700" title={tx.amount_unavailable_reason}>n/a</span>
                              : (tx.debit > 0 ? `${fmtAmt(tx.debit)}` : '--')}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {tx.amount_available === false
                              ? <span className="text-amber-700" title={tx.amount_unavailable_reason}>n/a</span>
                              : (tx.credit > 0 ? `${fmtAmt(tx.credit)}` : '--')}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="secondary" className={`text-[9px] ${STATUS_COLORS[tx.status] || STATUS_COLORS.unknown}`}>{tx.status}</Badge>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground max-w-[180px] truncate">{tx.notes}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* === ACCOUNTING-GRADE REPORTS TAB === */}
        {scopeResolved && tab === 'reports' && (
          <AccountingGradeReports
            scopeLabel={t(`accountingScope.reports.${scopeState}`)}
            fromDate={fromDate}
            toDate={toDate}
            scopeKey={scopeKey}
          />
        )}

        {/* === COUNTERPARTIES TAB === */}
        {scopeResolved && tab === 'counterparties' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Counterparties ({counterparties.length})</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="counterparty-table">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Type</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Name</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">ID</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Total Received</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Total Payable</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Commission</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Escrow</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Net Position</th>
                      <th className="text-center px-3 py-2 font-medium text-muted-foreground">Movements</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {counterparties.length === 0 ? (
                      <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">Loading counterparties...</td></tr>
                    ) : counterparties.map((cp, i) => (
                      <tr key={`${cp.counterparty_type}-${cp.counterparty_id}`} className="border-b hover:bg-muted/20 transition-colors" data-testid={`cp-row-${i}`}>
                        <td className="px-3 py-2"><Badge variant="outline" className="text-[9px] capitalize">{cp.counterparty_type}</Badge></td>
                        <td className="px-3 py-2 font-medium max-w-[180px] truncate">{cp.counterparty_name}</td>
                        <td className="px-3 py-2 text-muted-foreground font-mono text-[9px] max-w-[120px] truncate" title={cp.counterparty_id}>{cp.counterparty_id}</td>
                        <td className="px-3 py-2 text-right">{fmtAmt(cp.total_received)}</td>
                        <td className="px-3 py-2 text-right">{fmtAmt(cp.total_payable)}</td>
                        <td className="px-3 py-2 text-right">{fmtAmt(cp.total_commission)}</td>
                        <td className="px-3 py-2 text-right">{fmtAmt(cp.escrow_balance)}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmtAmt(cp.net_position)}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{cp.movement_count || 0}</td>
                        <td className="px-3 py-2">
                          {cp.limitation ? (
                            <span className="text-[9px] text-amber-600 italic">partial</span>
                          ) : (
                            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2"
                              onClick={() => fetchStatement(cp.counterparty_type, cp.counterparty_id)}
                              data-testid={`view-statement-${cp.counterparty_type}-${i}`}>
                              <Eye className="h-3 w-3 mr-1" /> Statement
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* === STATEMENT TAB === */}
        {tab === 'statement' && statement && (
          <>
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <Button size="sm" variant="ghost" className="text-xs mb-2 -ml-2 text-muted-foreground"
                      onClick={() => handleTabChange('counterparties')} data-testid="stmt-back-btn">
                      <ChevronLeft className="h-3 w-3 mr-1" /> Back to Counterparties
                    </Button>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Statement for</p>
                    <p className="text-lg font-bold">{statement.counterparty_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{statement.counterparty_type} | {statement.movement_count} movements</p>
                  </div>
                  {statement.mixed_currency ? (
                    <div className="text-sm" data-testid="statement-totals-by-currency">
                      <p className="text-xs text-muted-foreground mb-1">Totals per currency (no cross-currency total)</p>
                      {(statement.totals_by_currency || []).map(b => (
                        <div key={b.currency} className="flex gap-4 justify-end text-xs">
                          <span className="font-semibold">{b.currency}</span>
                          <span className="text-emerald-600">Dr {fmtAmt(b.debit)}</span>
                          <span className="text-orange-600">Cr {fmtAmt(b.credit)}</span>
                          <span className="font-bold">Net {fmtAmt(b.net_movement)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                  <div className="flex gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Total Debit</p>
                      <p className="font-bold text-emerald-600" title="Money received by TreatX from this counterparty">{statement.currency || ''} {fmtAmt(statement.total_debit)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Total Credit</p>
                      <p className="font-bold text-orange-600" title="Money paid out by TreatX to this counterparty">{statement.currency || ''} {fmtAmt(statement.total_credit)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Closing Balance</p>
                      <p className="font-bold" title="Debit minus Credit — positive means TreatX holds funds">{statement.currency || ''} {fmtAmt(statement.closing_balance)}</p>
                    </div>
                  </div>
                  )}
                </div>
                <div className="mt-3 bg-slate-50 rounded-md px-3 py-2 text-[10px] text-muted-foreground" data-testid="ledger-legend">
                  <span className="font-medium">Ledger convention:</span> Debit = money received by TreatX from counterparty. Credit = money paid out by TreatX to counterparty. Balance = Debit - Credit (positive = TreatX holds funds).
                  {statement.basis_note && (
                    <span className="block mt-1" data-testid="statement-basis-note">{statement.basis_note}</span>
                  )}
                </div>
                {statement.closing_balance_complete === false && (
                  <div className="mt-2 bg-amber-50 border border-amber-300 rounded-md px-3 py-2 text-[11px] text-amber-900"
                    data-testid="statement-incomplete-note">
                    {statement.amounts_unavailable_note}
                    {statement.amounts_unavailable_reasons?.length > 0 && (
                      <span className="block mt-1 font-mono">
                        {statement.amounts_unavailable_reasons.join(' · ')}
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="px-0 pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="statement-table">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground">Reference</th>
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground">Movement</th>
                        <th className="text-right px-3 py-2 font-medium text-muted-foreground">Debit</th>
                        <th className="text-right px-3 py-2 font-medium text-muted-foreground">Credit</th>
                        <th className="text-right px-3 py-2 font-medium text-muted-foreground">Balance</th>
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground">Status</th>
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(statement.movements || []).map((m, i) => {
                        const ml = MOVEMENT_LABELS[m.movement_type] || {};
                        // ACCOUNTING-TRUTH-01 (B2) — an unreadable amount shows
                        // as UNAVAILABLE, never as '--' (which reads as nothing)
                        // and never as 0.00.
                        const unavailable = m.amount_available === false;
                        const amt = (v) => (unavailable
                          ? <span className="text-amber-700" title={m.amount_unavailable_reason}>n/a</span>
                          : (v > 0 ? fmtAmt(v) : '--'));
                        return (
                          <tr key={`${m.reference}-${i}`} className="border-b hover:bg-muted/20" data-testid={`stmt-row-${i}`}>
                            <td className="px-3 py-2 text-muted-foreground">{fmtDate(m.date)}</td>
                            <td className="px-3 py-2 font-mono text-[10px]">{m.reference}</td>
                            <td className="px-3 py-2">
                              <span className={ml.color || ''}>{m.description_label || ml.label || m.movement_type}</span>
                            </td>
                            <td className="px-3 py-2 text-right">{amt(m.debit)}</td>
                            <td className="px-3 py-2 text-right">{amt(m.credit)}</td>
                            <td className="px-3 py-2 text-right font-medium">
                              {m.balance_after != null ? fmtAmt(m.balance_after) : '--'}
                              {m.balance_complete === false && (
                                <span className="ml-1 text-[9px] text-amber-700"
                                  title={m.balance_note} data-testid={`stmt-balance-incomplete-${i}`}>*</span>
                              )}
                            </td>
                            <td className="px-3 py-2"><Badge variant="secondary" className={`text-[9px] ${STATUS_COLORS[m.status] || ''}`}>{m.status}</Badge></td>
                            <td className="px-3 py-2 text-muted-foreground max-w-[200px] truncate">{m.notes}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}